import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  CloseOutlined,
  DeleteOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Button, Flex, InputNumber, Slider, Tooltip } from 'antd';
import { SideWithAsset } from 'shared-types';

import useProductsStore from 'hooks/stores/useProductsStore';
import useTrimboxCrop from 'hooks/useTrimboxCrop';

import API from 'services/API';
import { insertBlankPageAt } from 'services/blankPageService';

import {
  clearSidePreviewRequested,
  hasRequestedSidePreview,
  markSidePreviewRequested,
} from 'utils/sidePreviewRequests';

type Slot = {
  id: string;
  pageNumber: number;
  previewImageUrl?: string;
};

type Spread = [Slot | null, Slot | null];

type BookPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  productId: string;
};

type PendingAction = { type: 'insert' | 'remove' };

const CONTENT_PADDING = 32;
const SLOT_GAP = 8;
const MIN_SLOT_WIDTH = 160;
const SLIDER_COMMIT_DELAY_MS = 500;

// Spread 0 is the cover (page 1 alone); every slot index after that pairs up two at a time.
// In single-slot mode (products with only 1-2 sides, e.g. banners/signs — see isSingleSlotMode)
// there's no pairing at all: one slot per spread, so the spread index equals the slot index.
const spreadIndexForSlotIndex = (index: number, singleSlotMode: boolean) =>
  singleSlotMode ? Math.max(index, 0) : index <= 0 ? 0 : 1 + Math.floor((index - 1) / 2);

const hasPreviewImage = (slot: Slot | null) =>
  Boolean(slot && slot.previewImageUrl && slot.previewImageUrl !== '');

const BookPreviewModal = ({ isOpen, onClose, sessionId, productId }: BookPreviewModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sides = useProductsStore(
    (state) => (state.products.find((p) => p.id === productId)?.sides ?? []) as SideWithAsset[],
  );

  const contentRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const sliderCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trimbox aspect ratio + bleed-crop geometry, shared with the booklayout grid (see
  // hooks/useTrimboxCrop.ts for the why).
  const { aspectRatio, imageStyle: trimboxImageStyle } = useTrimboxCrop();

  const [spreadIndex, setSpreadIndex] = useState(0);
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [sliderDragValue, setSliderDragValue] = useState<number | null>(null);
  // Pages we ourselves created as blanks in this session — only these get a delete
  // affordance, since the modal has no way to tell a "real" blank page apart otherwise.
  const [insertedBlankSideIds, setInsertedBlankSideIds] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reset state whenever the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setSpreadIndex(0);
      setSliderDragValue(null);
      setInsertedBlankSideIds(new Set());
      setPendingAction(null);
      setActionError(null);
      if (sliderCommitTimerRef.current) clearTimeout(sliderCommitTimerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(
    () => () => {
      if (sliderCommitTimerRef.current) clearTimeout(sliderCommitTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    const el = contentRef.current;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setContentSize({ width: rect.width, height: rect.height });
    };

    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [isOpen]);

  // Source of truth for how many slots to show is `sides` itself now — each slot renders
  // the side's own already-fully-rendered `previewImageUrl` (same image the central editor
  // shows), so there's no combined-PDF round trip involved in viewing at all.
  const slots: Slot[] = sides.map((side, i) => ({
    id: side.id,
    pageNumber: i + 1,
    previewImageUrl: side.previewImageUrl,
  }));

  // Products with only 1-2 sides (e.g. banners, signs) aren't "books" — there's no spine, so
  // pairing page 1 on the right and page 2 on the left (as a real book spread would) looks wrong.
  // Show each side centered on its own instead. Anything with 3+ sides keeps the normal book
  // layout (cover alone, then two-page spreads).
  const isSingleSlotMode = slots.length > 0 && slots.length <= 2;

  // Build spreads: page 1 stands alone on the right, then pairs of consecutive pages —
  // unless isSingleSlotMode, in which case every side gets its own single-slot spread.
  const spreads: Spread[] = [];
  if (isSingleSlotMode) {
    slots.forEach((slot) => spreads.push([slot, null]));
  } else if (slots.length > 0) {
    spreads.push([null, slots[0]]);
    for (let i = 1; i < slots.length; i += 2) {
      spreads.push([slots[i], slots[i + 1] ?? null]);
    }
  }

  const currentSpread = spreads[spreadIndex] ?? [null, null];
  const canGoPrev = spreadIndex > 0;
  const canGoNext = spreadIndex < spreads.length - 1;

  // 1-based positions of the currently visible slots within the book, for the
  // page label, the jump-to-page input, and the scrub slider below.
  const leftPageNumber = currentSpread[0] ? slots.indexOf(currentSpread[0]) + 1 : null;
  const rightPageNumber = currentSpread[1] ? slots.indexOf(currentSpread[1]) + 1 : null;
  const currentPageNumber = leftPageNumber ?? rightPageNumber ?? 1;

  const [pageInputValue, setPageInputValue] = useState<number | null>(currentPageNumber);
  useEffect(() => {
    setPageInputValue(currentPageNumber);
    // Only resync when the displayed page actually changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageNumber]);

  // The book viewer only ever shows the current spread's one or two slots, so — unlike the
  // sidebar's virtualized list (which needs an IntersectionObserver + visibility delay to
  // avoid a thundering herd while scrolling past many items) — a missing preview can be
  // requested immediately. The shared request-dedup guards (also used by SortablePageItem)
  // keep this from double-firing against a request the sidebar already made for the same side.
  const leftSlotId = currentSpread[0]?.id;
  const leftSlotPreviewUrl = currentSpread[0]?.previewImageUrl;
  const rightSlotId = currentSpread[1]?.id;
  const rightSlotPreviewUrl = currentSpread[1]?.previewImageUrl;

  useEffect(() => {
    [currentSpread[0], currentSpread[1]].forEach((slot) => {
      if (!slot || hasPreviewImage(slot) || hasRequestedSidePreview(slot.id)) return;
      markSidePreviewRequested(slot.id);
      API.generateSidePreview(sessionId, slot.id).catch(() => {
        clearSidePreviewRequested(slot.id);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, leftSlotId, leftSlotPreviewUrl, rightSlotId, rightSlotPreviewUrl]);

  const jumpToPage = (rawValue: number | null) => {
    if (rawValue === null || slots.length === 0) return;
    const clamped = Math.min(Math.max(Math.round(rawValue), 1), slots.length);
    setSpreadIndex(spreadIndexForSlotIndex(clamped - 1, isSingleSlotMode));
  };

  const handleSliderChange = (value: number) => {
    setSliderDragValue(value);
    if (sliderCommitTimerRef.current) clearTimeout(sliderCommitTimerRef.current);
    sliderCommitTimerRef.current = setTimeout(() => {
      jumpToPage(value);
      setSliderDragValue(null);
    }, SLIDER_COMMIT_DELAY_MS);
  };

  // While the slider handle is settling, the label previews the spread the
  // page number would land on, without touching the actually rendered spread.
  const isDraggingSlider = sliderDragValue !== null;
  const displayedPageNumber = sliderDragValue ?? currentPageNumber;
  const previewSpread = isDraggingSlider
    ? (spreads[
        spreadIndexForSlotIndex(
          Math.min(Math.max(displayedPageNumber, 1), slots.length) - 1,
          isSingleSlotMode,
        )
      ] ?? currentSpread)
    : currentSpread;
  const previewLeftPageNumber = previewSpread[0] ? slots.indexOf(previewSpread[0]) + 1 : null;
  const previewRightPageNumber = previewSpread[1] ? slots.indexOf(previewSpread[1]) + 1 : null;

  // Insert/bubble logic lives in services/blankPageService so the booklayout grid behaves
  // exactly the same as this viewer.
  const handleInsertBlankPageAt = async (targetIndex: number) => {
    if (pendingAction) return;
    setActionError(null);
    setPendingAction({ type: 'insert' });
    try {
      const { sideId, index } = await insertBlankPageAt({
        sessionId,
        productId,
        targetIndex,
        queryClient,
      });
      setInsertedBlankSideIds((prev) => new Set(prev).add(sideId));
      setSpreadIndex(spreadIndexForSlotIndex(index, isSingleSlotMode));
    } catch (error) {
      console.error('Insert blank page failed', error);
      setActionError(t('BookPreview.InsertBlankPageError') ?? 'Failed to insert blank page');
    } finally {
      setPendingAction(null);
    }
  };

  const handleRemoveBlankPage = async (sideId: string) => {
    if (pendingAction) return;
    setActionError(null);
    setPendingAction({ type: 'remove' });
    try {
      const removedIndex = slots.findIndex((slot) => slot.id === sideId);

      await API.deleteSide(sessionId, sideId);
      await queryClient.invalidateQueries('products');

      setInsertedBlankSideIds((prev) => {
        const next = new Set(prev);
        next.delete(sideId);
        return next;
      });
      setSpreadIndex(spreadIndexForSlotIndex(Math.max(removedIndex - 1, 0), isSingleSlotMode));
    } catch (error) {
      console.error('Remove blank page failed', error);
      setActionError(t('BookPreview.RemoveBlankPageError') ?? 'Failed to remove blank page');
    } finally {
      setPendingAction(null);
    }
  };

  const availableWidth = Math.max(
    0,
    contentSize.width - CONTENT_PADDING * 2 - (isSingleSlotMode ? 0 : SLOT_GAP),
  );
  const availableHeight = Math.max(0, contentSize.height - CONTENT_PADDING * 2);
  const slotWidth = Math.max(
    MIN_SLOT_WIDTH,
    Math.min(
      isSingleSlotMode ? availableWidth : availableWidth / 2,
      availableHeight / aspectRatio || Infinity,
    ),
  );
  const slotHeight = slotWidth * aspectRatio;

  // Hover is tracked as a raw X offset within the row (not per-element enter/leave),
  // and the active zone is derived from it fresh on every render. This is what makes
  // the overlay update immediately after a spread change: the cursor hasn't moved,
  // but the geometry-based lookup below re-evaluates against the new content anyway,
  // whereas DOM enter/leave events tied to the old slot's id would never re-fire.
  const hasLeftSlot = currentSpread[0] !== null;
  const hasRightSlot = currentSpread[1] !== null;

  type ZoneId = 'nav-prev' | 'insert-left' | 'insert-right' | 'nav-next';
  let activeZone: ZoneId | null = null;
  if (hoverX !== null) {
    if (isSingleSlotMode) {
      // A standalone centered page has no spine, so there's no insert-here overlay here —
      // just the outer thirds to turn to the previous/next side.
      if (hoverX >= 0 && hoverX < slotWidth / 3) {
        activeZone = 'nav-prev';
      } else if (hoverX >= (slotWidth * 2) / 3 && hoverX < slotWidth) {
        activeZone = 'nav-next';
      }
    } else if (hasLeftSlot && hoverX >= 0 && hoverX < slotWidth) {
      activeZone = hoverX < slotWidth / 3 ? 'nav-prev' : 'insert-left';
    } else if (
      hasRightSlot &&
      hoverX >= slotWidth + SLOT_GAP &&
      hoverX < slotWidth * 2 + SLOT_GAP
    ) {
      const rightLocalX = hoverX - slotWidth - SLOT_GAP;
      activeZone = rightLocalX < (slotWidth * 2) / 3 ? 'insert-right' : 'nav-next';
    }
  }
  if (activeZone === 'nav-prev' && !canGoPrev) activeZone = null;
  if (activeZone === 'nav-next' && !canGoNext) activeZone = null;

  const ZONE_SIBLINGS: Record<ZoneId, ZoneId> = {
    'nav-prev': 'insert-left',
    'insert-left': 'nav-prev',
    'insert-right': 'nav-next',
    'nav-next': 'insert-right',
  };
  const zoneOpacity = (zone: ZoneId) => {
    if (activeZone === zone) return 1;
    if (activeZone && ZONE_SIBLINGS[activeZone] === zone) return 0.5;
    return 0;
  };

  const handleRowMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = rowRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverX(event.clientX - rect.left);
  };
  const handleRowMouseLeave = () => setHoverX(null);

  // Outer third of the page turns the book (arrow), inner two-thirds (against the
  // gutter) offers the blank-page insert — mirrors how physical books are handled.
  // Hovering either zone dims the other to half opacity, so the sibling option
  // stays visible as a hint rather than disappearing entirely.
  const renderNavZone = (side: 'left' | 'right') => {
    const canNav = side === 'left' ? canGoPrev : canGoNext;
    if (!canNav) return null;

    const zoneId: ZoneId = side === 'left' ? 'nav-prev' : 'nav-next';
    const opacity = zoneOpacity(zoneId);

    return (
      <div
        onClick={() =>
          !pendingAction && setSpreadIndex((prev) => prev + (side === 'left' ? -1 : 1))
        }
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          [side === 'left' ? 'left' : 'right']: 0,
          width: '33.3333%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
          padding: 12,
          boxSizing: 'border-box',
          backgroundColor: 'rgba(0,0,0,0.35)',
          opacity,
          transition: 'opacity 0.15s',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.9)',
            color: '#1a1a1a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          {side === 'left' ? <LeftOutlined /> : <RightOutlined />}
        </div>
      </div>
    );
  };

  const renderInsertZone = (slot: Slot, side: 'left' | 'right') => {
    const zoneId: ZoneId = side === 'left' ? 'insert-left' : 'insert-right';
    const opacity = zoneOpacity(zoneId);

    return (
      <div
        onClick={() => handleInsertBlankPageAt(slots.indexOf(slot))}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          [side === 'left' ? 'right' : 'left']: 0,
          width: '66.6667%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: 16,
          boxSizing: 'border-box',
          textAlign: 'center',
          color: '#fff',
          backgroundColor: 'rgba(0,0,0,0.55)',
          opacity,
          transition: 'opacity 0.15s',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#35b3ee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          <PlusOutlined />
        </div>
        <div style={{ fontWeight: 600 }}>{t('BookPreview.InsertBlankPageHere')}</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>{t('BookPreview.InsertPageShiftHint')}</div>
      </div>
    );
  };

  const renderSlot = (slot: Slot | null, side: 'left' | 'right' | 'center') => {
    const baseStyle: React.CSSProperties = {
      width: slotWidth,
      height: slotHeight,
      backgroundColor: '#fff',
      boxShadow:
        side === 'left'
          ? '-2px 0 6px rgba(0,0,0,0.25)'
          : side === 'right'
            ? '2px 0 6px rgba(0,0,0,0.25)'
            : '0 2px 10px rgba(0,0,0,0.3)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0,
    };

    if (!slot) {
      return <div style={{ ...baseStyle, visibility: 'hidden', boxShadow: 'none' }} />;
    }

    const isKnownBlank = insertedBlankSideIds.has(slot.id);

    return (
      <div style={baseStyle}>
        {hasPreviewImage(slot) ? (
          // previewImageUrl is the full trim+bleed page with a bleed marker baked in. It's
          // rendered oversized (by imageHorizontal/VerticalScale) and shifted up/left so that
          // only the trimbox — positioned at the bleed insets computed above — shows inside
          // this container's overflow:hidden bounds.
          <img
            src={slot.previewImageUrl}
            alt={`Page ${slot.pageNumber}`}
            style={trimboxImageStyle}
          />
        ) : (
          <ClipLoader color="#35b3ee" size={24} />
        )}
        {side === 'center' ? (
          <>
            {renderNavZone('left')}
            {renderNavZone('right')}
          </>
        ) : (
          <>
            {renderInsertZone(slot, side)}
            {renderNavZone(side)}
          </>
        )}
        {isKnownBlank && (
          <Tooltip title={t('BookPreview.RemoveBlankPage')}>
            <Button
              size="small"
              shape="circle"
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveBlankPage(slot.id)}
              style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        zIndex: 1000,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#1a1a1a',
      }}
      aria-label="Book Preview Modal"
    >
      <Flex
        justify="space-between"
        align="center"
        style={{ padding: '12px 16px', flex: '0 0 auto' }}
      >
        <h3 style={{ color: '#fff', margin: 0 }}>{t('BookPreview.Title')}</h3>
        <Tooltip title={t('BookPreview.Close')}>
          <Button icon={<CloseOutlined />} shape="circle" onClick={onClose} />
        </Tooltip>
      </Flex>

      <div
        ref={contentRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          boxSizing: 'border-box',
          padding: CONTENT_PADDING,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {slots.length === 0 ? (
          <h2 style={{ color: '#fff' }}>{t('BookPreview.Error')}</h2>
        ) : (
          <div
            ref={rowRef}
            onMouseMove={handleRowMouseMove}
            onMouseLeave={handleRowMouseLeave}
            style={{ display: 'flex', alignItems: 'center', gap: isSingleSlotMode ? 0 : SLOT_GAP }}
          >
            {isSingleSlotMode ? (
              renderSlot(currentSpread[0], 'center')
            ) : (
              <>
                {renderSlot(currentSpread[0], 'left')}
                {renderSlot(currentSpread[1], 'right')}
              </>
            )}
          </div>
        )}

        {pendingAction && (
          <Flex
            vertical
            align="center"
            justify="center"
            gap={12}
            style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)' }}
          >
            <ClipLoader color="#35b3ee" size={40} />
            <span style={{ color: '#fff' }}>
              {t(
                pendingAction.type === 'insert'
                  ? 'BookPreview.InsertingBlankPage'
                  : 'BookPreview.RemovingPage',
              )}
            </span>
          </Flex>
        )}
      </div>

      {actionError && (
        <div style={{ color: '#ff7875', textAlign: 'center', padding: '0 16px' }}>
          {actionError}
        </div>
      )}

      {slots.length > 0 && (
        <Flex vertical align="center" gap={8} style={{ padding: '16px', flex: '0 0 auto' }}>
          <Flex justify="center" align="center" gap={12}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => setSpreadIndex((prev) => prev - 1)}
              disabled={!canGoPrev || !!pendingAction}
            />
            <span style={{ color: '#fff', minWidth: 100, textAlign: 'center' }}>
              {previewLeftPageNumber && previewRightPageNumber
                ? t('BookPreview.PagesLabel', {
                    left: previewLeftPageNumber,
                    right: previewRightPageNumber,
                  })
                : t('BookPreview.PageLabel', { page: displayedPageNumber })}
            </span>
            <InputNumber
              size="small"
              min={1}
              max={slots.length}
              value={pageInputValue}
              onChange={setPageInputValue}
              onPressEnter={() => jumpToPage(pageInputValue)}
              onBlur={() => jumpToPage(pageInputValue)}
              disabled={!!pendingAction}
              style={{ width: 64 }}
            />
            <Button
              icon={<RightOutlined />}
              onClick={() => setSpreadIndex((prev) => prev + 1)}
              disabled={!canGoNext || !!pendingAction}
            />
          </Flex>
          <Flex align="center" gap={8} style={{ width: 320, maxWidth: '80vw' }}>
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>1</span>
            <Slider
              value={displayedPageNumber}
              min={1}
              max={slots.length}
              step={1}
              onChange={handleSliderChange}
              disabled={!!pendingAction}
              tooltip={{ formatter: (value) => `${value}` }}
              marks={{ 1: '', [slots.length]: '' }}
              styles={{
                rail: { backgroundColor: 'rgba(255,255,255,0.3)' },
                track: { backgroundColor: '#35b3ee' },
              }}
              style={{ flex: '1 1 auto', margin: '0 4px' }}
            />
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>{slots.length}</span>
          </Flex>
        </Flex>
      )}
    </div>,
    document.body,
  );
};

export default BookPreviewModal;
